import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/UserActivity.css';
import completedService from '../services/completedService';
import userService from '../services/userService';
import { generateCalendarDays, mapActivitiesToCalendar, calculateActivityStats } from '../utils/calendarUtils';
import Cookies from 'js-cookie';
import ActivityCalendar from '../components/ActivityCalendar';
import ActivityDetails from '../components/ActivityDetails';
import { isAuthenticated } from '../utils/authUtils';

const UserActivity = () => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [calendarDays, setCalendarDays] = useState([]);
    const [activityData, setActivityData] = useState([]);
    const [detailData, setDetailData] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [filterType, setFilterType] = useState('all');
    const [stats, setStats] = useState({ totalActivities: 0, totalDays: 0, byType: {} });
    const [compareAll, setCompareAll] = useState(false);
    const [allUserActivity, setAllUserActivity] = useState([]);
    const [rangeStart, setRangeStart] = useState(() => {
        const d = new Date();
        d.setDate(1);
        return d.toISOString().split('T')[0];
    });
    const [rangeEnd, setRangeEnd] = useState(() => {
        const d = new Date();
        return d.toISOString().split('T')[0];
    });
    const [compareLoading, setCompareLoading] = useState(false);
    const [chartType, setChartType] = useState('bars'); // 'bars', 'donut', 'area'
    
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
      // Check if the user is authenticated and if they are an admin
    useEffect(() => {
        const checkAuth = async () => {
            const auth = isAuthenticated();
            if (!auth) {
                navigate('/login');
                return;
            }
            
            // Check if user is admin
            const isAdmin = Cookies.get("isAdmin") === "true";
            if (!isAdmin) {
                navigate('/members');
                return;
            }
            
            loadData();
        };
        
        checkAuth();
    }, [navigate]);
    
    // Load calendar data when the month changes or user filter changes
    useEffect(() => {
        const loadCalendarData = async () => {
            if (!selectedUser) return;
            
            setLoading(true);
              try {
                // Get the first and last day of the month
                const year = currentDate.getFullYear();
                const month = currentDate.getMonth();
                const firstDay = new Date(year, month, 1);
                const lastDay = new Date(year, month + 1, 0);
                
                // Format dates for API
                const startDate = firstDay.toISOString().split('T')[0];
                const endDate = lastDay.toISOString().split('T')[0];
                
                console.log('📅 Loading data for date range:', { startDate, endDate });
                console.log('👤 Selected user:', selectedUser);
                
                // Generate calendar days
                const days = generateCalendarDays(currentDate);
                
                // Fetch activity data
                const filters = {
                    userId: selectedUser.id,
                    startDate,
                    endDate
                };
                
                if (filterType !== 'all') {
                    filters.itemType = filterType;
                }
                
                console.log('🔍 Fetching completed counts with filters:', filters);
                const data = await completedService.getCompletedCounts(filters);
                console.log('📊 Received completed count data:', data);
                
                // Calculate statistics
                // Normalize count field to numbers before stats mapping
                const normalized = data.map(r => ({ ...r, count: parseInt(r.count, 10) || 0 }));
                const activityStats = calculateActivityStats(normalized);
                console.log('📈 Activity stats calculated:', activityStats);
                setStats(activityStats);
                
                // Map activities to calendar
                const updatedDays = mapActivitiesToCalendar(normalized, days);
                setCalendarDays(updatedDays);
                setActivityData(normalized);
            } catch (error) {
                console.error('❌ Failed to load calendar data:', error);
            } finally {
                setLoading(false);
            }
        };
        
        loadCalendarData();
    }, [currentDate, selectedUser, filterType]);    // Function to load users and initial data
    const loadData = async () => {
        try {
            console.log('🔍 Loading users data...');
            // Load users using userService
            const userData = await userService.getAllUsers();
            console.log('👥 Users data received:', userData);
            setUsers(userData);
            setFilteredUsers(userData);
            
            // Set first user as default
            if (userData.length > 0) {
                console.log('👤 Setting default user:', userData[0]);
                setSelectedUser(userData[0]);
            } else {
                console.warn('⚠️ No users found in retrieved data');
            }
        } catch (error) {
            console.error('❌ Error loading users data:', error);
        }
    };
    
    // Fetch all users' activity counts for comparison
    useEffect(() => {
        if (!compareAll) return;
        const fetchAllUserActivity = async () => {
            setCompareLoading(true);
            try {
                const userCounts = await Promise.all(users.map(async (user) => {
                    const filters = { userId: user.id, startDate: rangeStart, endDate: rangeEnd };
                    const data = await completedService.getCompletedCounts(filters);
                    // FIX: item.count arrives as a string (Sequelize COUNT). Coerce to number to avoid string concatenation like 0 + "5" -> "05".
                    const total = data.reduce((sum, item) => sum + (parseInt(item.count, 10) || 0), 0);
                    return { user, total };
                }));
                setAllUserActivity(userCounts);
            } catch (err) {
                setAllUserActivity([]);
            } finally {
                setCompareLoading(false);
            }
        };
        fetchAllUserActivity();
    }, [compareAll, users, rangeStart, rangeEnd]);

    // Handle month navigation
    const navigateMonth = (direction) => {
        setCurrentDate(prevDate => {
            const newDate = new Date(prevDate);
            newDate.setMonth(prevDate.getMonth() + direction);
            return newDate;
        });
    };
      // Handle date selection
    const handleDateSelect = async (date) => {
        if (!selectedUser) {
            console.warn('⚠️ No user selected, cannot fetch activity details');
            return;
        }
        
        console.log('📅 Selected date:', date);
        console.log('👤 Selected user for details:', selectedUser);
        
        setSelectedDate(date);
        setDetailsLoading(true);
        
        try {
            // Fetch detailed activity for the selected date and user
            console.log('🔍 Fetching daily activity for user:', selectedUser.id, 'on date:', date);
            let details = await completedService.getUserDailyActivity(selectedUser.id, date);
            details = Array.isArray(details) ? details.map(d => ({ ...d, count: parseInt(d.count, 10) || 0 })) : details;
            console.log('📋 User daily activity details:', details);
            setDetailData(details);
        } catch (error) {
            console.error('❌ Failed to fetch activity details:', error);
            setDetailData([]);
        } finally {
            setDetailsLoading(false);
        }
    };
    
    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        
        // Filter users based on search term
        if (value.trim() === '') {
            setFilteredUsers(users);
        } else {
            const filtered = users.filter(user => 
                (user.username && user.username.toLowerCase().includes(value.toLowerCase())) || 
                (user.email && user.email.toLowerCase().includes(value.toLowerCase())) ||
                (user.name && user.name.toLowerCase().includes(value.toLowerCase()))
            );
            setFilteredUsers(filtered);
        }
    };
    
    // Handle user selection from dropdown
    const handleUserSelect = (user) => {
        setSelectedUser(user);
        setSearchTerm('');
        setDropdownOpen(false);
        setSelectedDate(null); // Reset selected date when user changes
    };
    
    // Handle click outside dropdown to close it
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    
    // Current month name for display
    const currentMonthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
    
    // Get all unique item types for filter options
    const itemTypes = Array.from(new Set(activityData.map(a => a.itemType))).filter(Boolean);
    
    return (
        <div className="activity-container">
            {/* Header Section */}
            <div className="activity-header">
                <div className="activity-header-content">
                    <h1>User Activity Tracking</h1>
                    <p className="activity-subtitle">Monitor and analyze user engagement across the platform</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="activity-main">
                <button className="mode-toggle-btn" onClick={() => setCompareAll((v) => !v)}>
                    {compareAll ? (
                        <><span>👤</span> Show Individual User</>
                    ) : (
                        <><span>📊</span> Compare All Users</>
                    )}
                </button>

                {compareAll ? (
                    <div className="compare-section">
                        <div className="compare-section-header">
                            <h2>User Activity Comparison</h2>
                        </div>
                        <div className="compare-section-body">
                            <div className="compare-header">
                                <label>
                                    <span>From:</span>
                                    <input type="date" value={rangeStart} onChange={e => setRangeStart(e.target.value)} />
                                </label>
                                <label>
                                    <span>To:</span>
                                    <input type="date" value={rangeEnd} onChange={e => setRangeEnd(e.target.value)} />
                                </label>
                            </div>

                            {/* Chart Type Toggle */}
                            <div className="chart-type-toggle">
                                <button
                                    className={`chart-type-btn ${chartType === 'bars' ? 'active' : ''}`}
                                    onClick={() => setChartType('bars')}
                                    title="Bar Chart"
                                >
                                    <span className="chart-icon">📊</span>
                                    <span className="chart-label">Bars</span>
                                </button>
                                <button
                                    className={`chart-type-btn ${chartType === 'donut' ? 'active' : ''}`}
                                    onClick={() => setChartType('donut')}
                                    title="Donut Chart"
                                >
                                    <span className="chart-icon">🍩</span>
                                    <span className="chart-label">Donut</span>
                                </button>
                                <button
                                    className={`chart-type-btn ${chartType === 'area' ? 'active' : ''}`}
                                    onClick={() => setChartType('area')}
                                    title="Area Chart"
                                >
                                    <span className="chart-icon">📈</span>
                                    <span className="chart-label">Area</span>
                                </button>
                            </div>

                            {compareLoading ? (
                                <div className="compare-loading">
                                    <div>Loading user activity...</div>
                                </div>
                            ) : (
                                <div className="chart-container">
                                    {chartType === 'bars' && <CompareUsersBar allUserActivity={allUserActivity} />}
                                    {chartType === 'donut' && <CompareUsersDonut allUserActivity={allUserActivity} />}
                                    {chartType === 'area' && <CompareUsersArea allUserActivity={allUserActivity} />}
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                <>
                    <div className="stats-summary">
                        <div className="stats-grid">
                            {Object.entries(stats.byType).map(([type, count]) => (
                                <div className="stat-card" key={type}>
                                    <div className="stat-value">{count}</div>
                                    <div className="stat-label">{type}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    <div className="date-controls">                <div className="date-navigation">
                            <button onClick={() => navigateMonth(-1)}>&laquo; Previous</button>
                            <div className="current-month">{currentMonthName}</div>
                            <button onClick={() => navigateMonth(1)}>Next &raquo;</button>
                        </div>
                        
                        <div className="filters">
                            <div className="user-search-dropdown" ref={dropdownRef}>
                                <div className="search-input-container">
                                    <input
                                        type="text"
                                        placeholder="Search for a user..."
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        onClick={() => setDropdownOpen(true)}
                                        className="user-search-input"
                                    />
                                    <div className="selected-user-display">
                                        {selectedUser && (
                                            <span>Current: {selectedUser.username || selectedUser.email || selectedUser.name || 'Unknown user'}</span>
                                        )}
                                    </div>
                                </div>
                                
                                {dropdownOpen && (
                                    <div className="user-dropdown">
                                        {filteredUsers.length > 0 ? (
                                            filteredUsers.map(user => (
                                                <div 
                                                    key={user.id} 
                                                    className={`user-option ${selectedUser?.id === user.id ? 'selected' : ''}`}
                                                    onClick={() => handleUserSelect(user)}
                                                >
                                                    {user.username || user.email || user.name || 'Unknown user'}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="no-results">No users found</div>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            <select 
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                            >
                                <option value="all">All Types</option>
                                {itemTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    
                    <ActivityCalendar 
                        calendarDays={calendarDays}
                        selectedDate={selectedDate}
                        onDateSelect={handleDateSelect}
                        loading={loading}
                    />
                    
                    {selectedDate && (
                        <ActivityDetails
                            activityData={detailData}
                            selectedDate={selectedDate}
                            selectedUser={selectedUser}
                            onClose={() => setSelectedDate(null)}
                            loading={detailsLoading}
                            calendarDays={calendarDays} // pass calendarDays for fallback
                        />
                    )}
                </>
                )}
            </div>
        </div>
    );
};

// Get rank class for user position
const getRankClass = (index) => {
    if (index === 0) return 'gold';
    if (index === 1) return 'silver';
    if (index === 2) return 'bronze';
    return 'normal';
};

// Color palette for charts
const CHART_COLORS = [
    '#F5A623', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444',
    '#f59e0b', '#06b6d4', '#ec4899', '#84cc16', '#6366f1'
];

// Horizontal bar graph component with rank badges
const CompareUsersBar = ({ allUserActivity }) => {
    if (!allUserActivity || allUserActivity.length === 0) {
        return (
            <div className="compare-empty">
                <p>No user activity data available.</p>
            </div>
        );
    }

    // Only show users with total > 0
    const filtered = allUserActivity.filter(u => u.total > 0);

    if (filtered.length === 0) {
        return (
            <div className="compare-empty">
                <p>No user activity found in this date range.</p>
            </div>
        );
    }

    const sorted = filtered.sort((a, b) => b.total - a.total);
    const max = Math.max(...sorted.map(u => u.total));

    return (
        <div className="compare-users-bar">
            {sorted.map(({ user, total }, index) => (
                <div key={user.id} className="user-bar-row">
                    <span className={`user-rank ${getRankClass(index)}`}>
                        {index < 3 ? ['🥇', '🥈', '🥉'][index] : index + 1}
                    </span>
                    <span className="user-bar-label">
                        {user.username || user.email || user.name || 'Unknown user'}
                    </span>
                    <div className="user-bar-outer">
                        <div
                            className="user-bar-inner"
                            style={{ width: max ? `${(total / max) * 100}%` : '0%' }}
                        />
                        <span className="user-bar-count">{total}</span>
                    </div>
                </div>
            ))}
        </div>
    );
};

// Donut Chart Component
const CompareUsersDonut = ({ allUserActivity }) => {
    if (!allUserActivity || allUserActivity.length === 0) {
        return (
            <div className="compare-empty">
                <p>No user activity data available.</p>
            </div>
        );
    }

    const filtered = allUserActivity.filter(u => u.total > 0);

    if (filtered.length === 0) {
        return (
            <div className="compare-empty">
                <p>No user activity found in this date range.</p>
            </div>
        );
    }

    const sorted = filtered.sort((a, b) => b.total - a.total);
    const total = sorted.reduce((sum, u) => sum + u.total, 0);

    // Calculate SVG donut segments
    const radius = 80;
    const strokeWidth = 30;
    const normalizedRadius = radius - strokeWidth / 2;
    const circumference = 2 * Math.PI * normalizedRadius;

    let accumulatedOffset = 0;
    const segments = sorted.map((item, index) => {
        const percentage = (item.total / total) * 100;
        const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
        const strokeDashoffset = -accumulatedOffset;
        accumulatedOffset += (percentage / 100) * circumference;

        return {
            ...item,
            percentage,
            strokeDasharray,
            strokeDashoffset,
            color: CHART_COLORS[index % CHART_COLORS.length]
        };
    });

    return (
        <div className="donut-chart-container">
            <div className="donut-chart">
                <svg viewBox="0 0 160 160" className="donut-svg">
                    {segments.map((segment, index) => (
                        <circle
                            key={segment.user.id}
                            cx="80"
                            cy="80"
                            r={normalizedRadius}
                            fill="transparent"
                            stroke={segment.color}
                            strokeWidth={strokeWidth}
                            strokeDasharray={segment.strokeDasharray}
                            strokeDashoffset={segment.strokeDashoffset}
                            transform="rotate(-90 80 80)"
                            className="donut-segment"
                            style={{ animationDelay: `${index * 0.1}s` }}
                        />
                    ))}
                    <text x="80" y="75" textAnchor="middle" className="donut-total-label">Total</text>
                    <text x="80" y="95" textAnchor="middle" className="donut-total-value">{total}</text>
                </svg>
            </div>
            <div className="donut-legend">
                {segments.map((segment, index) => (
                    <div key={segment.user.id} className="legend-item" style={{ animationDelay: `${index * 0.05}s` }}>
                        <span className="legend-color" style={{ background: segment.color }}></span>
                        <span className="legend-name">
                            {segment.user.username || segment.user.email || segment.user.name || 'Unknown'}
                        </span>
                        <span className="legend-value">{segment.total}</span>
                        <span className="legend-percent">{segment.percentage.toFixed(1)}%</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Area Chart Component
const CompareUsersArea = ({ allUserActivity }) => {
    if (!allUserActivity || allUserActivity.length === 0) {
        return (
            <div className="compare-empty">
                <p>No user activity data available.</p>
            </div>
        );
    }

    const filtered = allUserActivity.filter(u => u.total > 0);

    if (filtered.length === 0) {
        return (
            <div className="compare-empty">
                <p>No user activity found in this date range.</p>
            </div>
        );
    }

    const sorted = filtered.sort((a, b) => b.total - a.total);
    const max = Math.max(...sorted.map(u => u.total));

    // Chart dimensions - using viewBox so it scales
    const chartWidth = 900;
    const chartHeight = 350;
    const padding = { top: 20, right: 20, bottom: 60, left: 50 };
    const innerWidth = chartWidth - padding.left - padding.right;
    const innerHeight = chartHeight - padding.top - padding.bottom;

    // Calculate bar positions
    const barWidth = Math.min(60, (innerWidth / sorted.length) - 10);
    const barGap = (innerWidth - (barWidth * sorted.length)) / (sorted.length + 1);

    // Generate gradient area path
    const points = sorted.map((item, index) => {
        const x = padding.left + barGap + (index * (barWidth + barGap)) + (barWidth / 2);
        const y = padding.top + innerHeight - (item.total / max) * innerHeight;
        return { x, y, item };
    });

    const areaPath = `
        M ${points[0].x} ${padding.top + innerHeight}
        ${points.map(p => `L ${p.x} ${p.y}`).join(' ')}
        L ${points[points.length - 1].x} ${padding.top + innerHeight}
        Z
    `;

    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

    return (
        <div className="area-chart-container">
            <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="area-chart-svg">
                <defs>
                    <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#F5A623" stopOpacity="0.6" />
                        <stop offset="100%" stopColor="#F5A623" stopOpacity="0.05" />
                    </linearGradient>
                    <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#F5A623" />
                        <stop offset="50%" stopColor="#f59e0b" />
                        <stop offset="100%" stopColor="#fbbf24" />
                    </linearGradient>
                </defs>

                {/* Grid lines */}
                {[0, 0.25, 0.5, 0.75, 1].map((tick, i) => (
                    <g key={i}>
                        <line
                            x1={padding.left}
                            y1={padding.top + innerHeight * (1 - tick)}
                            x2={chartWidth - padding.right}
                            y2={padding.top + innerHeight * (1 - tick)}
                            stroke="#e5e7eb"
                            strokeDasharray="4 4"
                        />
                        <text
                            x={padding.left - 10}
                            y={padding.top + innerHeight * (1 - tick) + 4}
                            textAnchor="end"
                            className="axis-label"
                        >
                            {Math.round(max * tick)}
                        </text>
                    </g>
                ))}

                {/* Area fill */}
                <path d={areaPath} fill="url(#areaGradient)" className="area-path" />

                {/* Line */}
                <path d={linePath} fill="none" stroke="url(#lineGradient)" strokeWidth="3" className="line-path" />

                {/* Data points */}
                {points.map((point, index) => (
                    <g key={point.item.user.id} className="data-point-group" style={{ animationDelay: `${index * 0.1}s` }}>
                        <circle
                            cx={point.x}
                            cy={point.y}
                            r="8"
                            fill="white"
                            stroke={CHART_COLORS[index % CHART_COLORS.length]}
                            strokeWidth="3"
                            className="data-point"
                        />
                        <text
                            x={point.x}
                            y={point.y - 15}
                            textAnchor="middle"
                            className="point-value"
                        >
                            {point.item.total}
                        </text>
                    </g>
                ))}

                {/* X-axis labels */}
                {points.map((point, index) => (
                    <text
                        key={`label-${index}`}
                        x={point.x}
                        y={chartHeight - 15}
                        textAnchor="middle"
                        className="x-axis-label"
                        transform={`rotate(-30 ${point.x} ${chartHeight - 15})`}
                    >
                        {(point.item.user.username || point.item.user.email || point.item.user.name || 'User').slice(0, 10)}
                    </text>
                ))}
            </svg>
        </div>
    );
};

export default UserActivity;
